import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { supabase } from "../lib/supabase";
import api, { orgStore } from "../lib/api";
import { reportError } from "../lib/errorReporter";
import { ORG_BLOCKED_EVENT, pickDefaultOrgId } from "../lib/orgLifecycle";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [me, setMe] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [orgsLoaded, setOrgsLoaded] = useState(false);
  const [orgId, setOrgId] = useState(() => orgStore.get());

  const currentOrg = useMemo(
    () => orgs.find((o) => o.id === orgId) || null,
    [orgs, orgId],
  );

  // Session subscription lives in its own effect and never depends on state
  // that changes at runtime. That guarantees we install the ``onAuthStateChange``
  // listener exactly once and never leak subscriptions across renders.
  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        const { data } = await supabase.auth.getSession();
        if (!cancelled) setSession(data?.session || null);
      } catch (err) {
        // Non-fatal: without a session the app renders the login screen.
        reportError("auth.init.getSession", err);
        if (!cancelled) setSession(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    init();

    const sub = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      if (!nextSession) {
        setMe(null);
        setOrgs([]);
        setOrgsLoaded(false);
        orgStore.clear();
        setOrgId(null);
      }
    });

    return () => {
      cancelled = true;
      sub.data.subscription.unsubscribe();
    };
  }, []);

  // Track the latest known user id so that in-flight ``/me`` / ``/me/orgs``
  // responses for an old user cannot overwrite state for a newer one
  // (e.g. rapid logout + login as another account).
  const userIdRef = useRef(null);
  const userId = session?.user?.id || null;

  const loadMe = useCallback(async (activeId) => {
    try {
      const r = await api.get("/me", { skipOrgHeader: true });
      if (userIdRef.current === activeId) setMe(r.data);
    } catch (err) {
      reportError("auth.loadMe", err);
      if (userIdRef.current === activeId) setMe(null);
    }
  }, []);

  const refreshOrgs = useCallback(async (activeId) => {
    try {
      const r = await api.get("/me/organizations", { skipOrgHeader: true });
      if (userIdRef.current !== activeId) return;
      const list = r.data || [];
      setOrgs(list);
      // Phase 2D.1C — an explicit choice is kept; otherwise the first ACTIVE
      // membership wins, so a person with one archived and one usable
      // organization lands in the usable one instead of on the blocked screen.
      const next = pickDefaultOrgId(list, orgStore.get());
      if (next && next !== orgStore.get()) {
        orgStore.set(next);
        setOrgId(next);
      }
    } catch (err) {
      reportError("auth.refreshOrgs", err);
    } finally {
      if (userIdRef.current === activeId) setOrgsLoaded(true);
    }
  }, []);

  // Load user profile + org membership whenever the authenticated user changes.
  useEffect(() => {
    userIdRef.current = userId;
    if (!userId) return;
    setOrgsLoaded(false);
    loadMe(userId);
    refreshOrgs(userId);
  }, [userId, loadMe, refreshOrgs]);

  // Phase 2D.1C — a tenant request refused for a lifecycle reason means the
  // memberships held in memory are stale. Refresh them ONCE (throttled) so
  // <Guarded> swaps to the Bulgarian explanation instead of letting pages retry.
  const lastBlockedRefreshRef = useRef(0);
  useEffect(() => {
    const onBlocked = () => {
      const now = Date.now();
      if (now - lastBlockedRefreshRef.current < 10000) return;
      lastBlockedRefreshRef.current = now;
      if (userIdRef.current) refreshOrgs(userIdRef.current);
    };
    window.addEventListener(ORG_BLOCKED_EVENT, onBlocked);
    return () => window.removeEventListener(ORG_BLOCKED_EVENT, onBlocked);
  }, [refreshOrgs]);

  const switchOrg = useCallback((id) => {
    orgStore.set(id);
    setOrgId(id);
    // Soft signal so pages that hold cached data can refetch immediately.
    window.dispatchEvent(new Event("quowada:orgChanged"));
  }, []);

  const bootstrap = useCallback(async ({ fullName, organizationName }) => {
    const r = await api.post(
      "/auth/bootstrap",
      {
        full_name: fullName,
        organization_name: organizationName,
        default_currency: "EUR",
      },
      { skipOrgHeader: true },
    );
    // The bootstrap response is authoritative for the newly created org.
    orgStore.set(r.data.organization_id);
    setOrgId(r.data.organization_id);
    await refreshOrgs(userIdRef.current);
    return r.data;
  }, [refreshOrgs]);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      // Non-fatal: even if the remote sign-out call fails the local session
      // storage is cleared by the SDK and the ``onAuthStateChange`` listener
      // triggers the UI reset. Never log the raw error object here.
      reportError("auth.signOut", err);
    }
  }, []);

  // Stable provider value: unnecessary identity churn here would cause every
  // ``useAuth`` consumer to re-render on any parent update. All callbacks are
  // memoized so this object changes only when data or the user changes.
  const value = useMemo(
    () => ({
      loading,
      session,
      user: session?.user || null,
      me,
      orgs,
      orgsLoaded,
      orgId,
      currentOrg,
      switchOrg,
      refreshOrgs: () => refreshOrgs(userIdRef.current),
      bootstrap,
      signOut,
    }),
    [
      loading,
      session,
      me,
      orgs,
      orgsLoaded,
      orgId,
      currentOrg,
      switchOrg,
      refreshOrgs,
      bootstrap,
      signOut,
    ],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be inside AuthProvider");
  return v;
}
